// The main menu: a sheet down the left of the screen, with the map showing through beside it. The
// name and its one line at the top, Continue or Resume when there is a game to go back to, then the
// difficulties as a list, one row each: name, waves, cash, the best wave held at it, and Play.
import { MAPS, type TdMap } from '@thrash/sim';
import { GAME_NAME, TAGLINE } from './brand.ts';
import { button, el } from './hud.ts';
import { bestWaves } from './progress.ts';

export interface MenuActions {
  play(map: TdMap): void;
  /** The game in play, to go back to; null when none. */
  inPlay(): TdMap | null;
  resume(): void;
  /** A game kept in this browser, to carry on with; null when none. */
  saved(): { map: TdMap; wave: number } | null;
  continueSaved(): void;
  settings(anchor: HTMLElement): void;
  help(): void;
  credits(): void;
}

export interface Menu {
  show(): void;
  hide(): void;
  refresh(): void;
  readonly visible: boolean;
}

export function createMenu(a: MenuActions, build?: string): Menu {
  const wrap = el('div');
  wrap.id = 'menu';
  wrap.setAttribute('role', 'dialog');
  wrap.setAttribute('aria-label', 'Main menu');
  let visible = false;

  const render = () => {
    wrap.replaceChildren();
    const sheet = el('div', 'menu-sheet');

    const top = el('div', 'menu-top');
    const masthead = el('div', 'masthead');
    masthead.append(el('span', 'kicker', 'Tower defence'), el('h1', '', GAME_NAME), el('p', 'tagline', TAGLINE));
    const corner = el('div', 'menu-corner');
    const gear = button('Settings', () => a.settings(gear), { cls: 'round', icon: 'settings', iconOnly: true, title: 'Settings' });
    corner.append(gear, button('Help', () => a.help(), { cls: 'round', icon: 'help', iconOnly: true, title: 'How to play', key: '?' }));
    top.append(masthead, corner);
    sheet.appendChild(top);

    const playing = a.inPlay();
    const saved = playing ? null : a.saved();
    if (playing || saved) {
      const row = el('div', 'menu-continue');
      const text = el('div', 'row-text');
      if (playing) {
        text.append(el('span', 'kicker', 'In play'), el('b', `diff ${playing.difficulty}`, playing.name), el('span', 'meta', 'paused'));
        row.append(text, button('Resume', () => { hide(); a.resume(); }, { cls: 'primary', icon: 'play', key: 'Enter' }));
      } else if (saved) {
        text.append(el('span', 'kicker', 'Saved game'), el('b', `diff ${saved.map.difficulty}`, saved.map.name), el('span', 'meta', `wave ${Math.max(1, saved.wave)}`));
        row.append(text, button('Continue', () => { hide(); a.continueSaved(); }, { cls: 'primary', icon: 'play', key: 'Enter' }));
      }
      sheet.appendChild(row);
    }

    const list = el('div', 'map-list');
    list.appendChild(el('div', 'ph', 'Difficulty'));
    for (const m of MAPS) {
      const best = bestWaves(m.id);
      const row = el('div', 'map-row');
      const text = el('div', 'row-text');
      text.appendChild(el('b', `map-name diff ${m.difficulty}`, m.name));
      text.appendChild(el('span', 'meta', `${m.waves} waves · $${m.cash} to start · ${m.integrity} integrity`));
      text.appendChild(el('p', 'map-blurb', m.blurb));
      const bestEl = el('span', 'map-best');
      if (best > 0) {
        bestEl.textContent = `Best ${best} of ${m.waves}`;
        if (best >= m.waves) bestEl.append(' · ', el('span', 'held', 'held'));
      } else bestEl.textContent = 'Not played';
      text.appendChild(bestEl);
      row.append(text, button('Play', () => { hide(); a.play(m); }, { cls: 'primary' }));
      list.appendChild(row);
    }
    sheet.appendChild(list);

    const foot = el('div', 'menu-foot');
    foot.appendChild(button('Credits', () => a.credits(), { cls: 'link' }));
    if (build) foot.appendChild(el('span', 'build', `build ${build.slice(0, 12)}`));
    sheet.appendChild(foot);
    wrap.appendChild(sheet);
  };

  function show(): void {
    if (visible) { render(); return; }
    visible = true;
    render();
    document.body.appendChild(wrap);
    document.body.classList.add('menu-open');
  }

  function hide(): void {
    if (!visible) return;
    visible = false;
    wrap.remove();
    document.body.classList.remove('menu-open');
  }

  return { show, hide, refresh: () => { if (visible) render(); }, get visible() { return visible; } };
}
